import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {PasswordContainer} from '../../../../components/PasswordContainer';
import {SettingsPageContainer} from '../../../../components/Settings/SettingsPageContainer';
import {useHistory, useParams} from 'react-router-dom';
import {useQuestion} from '../../../../hooks/question';
import {useMessage} from '../../../../hooks/message';
import {useRPC} from '../../../../hooks/rpc';
import {useSelector} from 'react-redux';
import {IntercomModel} from '../../../../types/intercom';
import {useKeyboard} from '../../../../hooks/keyboard';
import {SettingInputComponent} from '../../../../components/Settings/SettingInputComponent';
import {SettingSelectComponent} from '../../../../components/Settings/SettingSelectComponent';
import {useMapSelector} from '../../../../hooks/map-selector';
import {SelectItem} from '../../../../components/Select';
import {DoorModel} from '../../../../types/door';
import {isValidSIPNo, isValidString} from '../../../../utils/validation';
import {CameraModel} from '../../../../types/control';
import {useLocalization} from "../../../../hooks/localization";

const BACK_URL = '/settings/intercom';

export const IntercomEditScreen = memo(function IntercomEditScreen() {
    const {t} = useLocalization();
    const params = useParams<{ id: string }>();
    const [setActiveInput, renderKeyboard, currentInput] = useKeyboard();
    const [setActiveNumpadInput, renderNumpadKeyboard, currentNumpadInput] = useKeyboard('numpad', true);
    const question = useQuestion();
    const {success, failure} = useMessage();
    const [rpc] = useRPC();
    const history = useHistory();

    const intercomList = useSelector(state => state.control.intercomList);
    const cameraList = useSelector(state => state.control.cameraList);
    const doorOptionList = useMapSelector(
        state => state.control.doorList,
        door => ({value: door, label: door.name}),
    ) || [];

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const selected = useMemo(() => intercomList.find(i => i.id === params?.id), [params?.id]);

    const [name, setName] = useState('');
    const [sipID, setSipID] = useState('');
    const [selectedDoorOptionList, setSelectedDoorOptionList] = useState<SelectItem<DoorModel>[]>([]);
    const cameraOptionList: SelectItem<CameraModel>[] = useMemo(() => cameraList.map(camera => ({
        label: camera.name,
        value: camera,
    })), [cameraList]);
    const [cameraOption, setCameraOption] = useState<SelectItem<CameraModel>>(
        selected == null ? null : (
            cameraOptionList.find(option => option.value.id === selected?.camera_id)
        ) || null,
    );

    const onBackPressed = useCallback(() => {
        history.push(BACK_URL);
    }, [history]);

    const isFieldsValid = useCallback((): boolean => {
        if (!isValidString(name)) {
            failure(t('pls_enter_a_valid_intercom_name'));

            return false;
        }

        if (cameraOption?.value == null) {
            failure(t('pls_select_a_valid_camera'));

            return false;
        }

        if (!isValidString(sipID)) {
            failure(t('pls_enter_a_valid_sip_no'));

            return false;
        }

        if (selectedDoorOptionList.length === 0) {
            failure(t('pls_select_door'));

            return false;
        }

        return true;
    }, [t, name, cameraOption?.value, sipID, selectedDoorOptionList.length, failure]);

    const onSavePressed = useCallback(() => {
        if (!isFieldsValid()) {
            return;
        }

        const intercomModel: IntercomModel = {
            id: selected?.id || null,
            name,
            camera_id: cameraOption.value.id,
            sip_id: sipID,
            door_id_list: selectedDoorOptionList.map(doorOption => doorOption.value.id),
        };

        return rpc.updateIntercom(intercomModel)
            .then(() => {
                success(t(intercomModel.id ? 'intercom_successfully_edited' : 'intercom_successfully_created'));
                onBackPressed();
            }).catch(failure);
    }, [
        t,
        isFieldsValid,
        selected?.id,
        name,
        sipID,
        selectedDoorOptionList,
        rpc,
        failure,
        success,
        onBackPressed,
        cameraOption?.value,
    ]);

    const onDeletePressed = useCallback(() => {
        question(t('are_you_sure_to_delete_the_intercom'), () => {
            return rpc.deleteIntercom(selected.id)
                .then(() => {
                    success(t('intercom_successfully_deleted'));
                    onBackPressed();
                })
                .catch(failure);
        });
    }, [t, failure, onBackPressed, question, rpc, selected?.id, success]);

    const onBlur = useCallback(() => {
        setActiveInput(null);
        setActiveNumpadInput(null);
    }, [setActiveInput, setActiveNumpadInput]);

    const buttons = useMemo(() =>
        [
            {
                text: t('delete'),
                className: 'mulberry-wood',
                onClick: onDeletePressed,
                hidden: !(selected),
            },
            {
                text: t('save'),
                className: 'green',
                onClick: onSavePressed,
            },
            {
                text: t('cancel'),
                className: 'red',
                url: BACK_URL,
            },
        ], [t, selected, onSavePressed, onDeletePressed],
    );

    useEffect(() => {
        if (params.id == null) {
            return;
        }

        const intercom = intercomList.find(intercomModel => intercomModel.id === params['id']);
        if (intercom == null) {
            return;
        }

        setName(intercom.name);
        setSipID(intercom.sip_id);
        setSelectedDoorOptionList(doorOptionList.filter(doorOption =>
            intercom.door_id_list.indexOf(doorOption.value.id) !== -1));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <PasswordContainer requiredRole='service' isFullscreen={true}>
            <SettingsPageContainer
                buttons={buttons}
                className='service-edit'
                backUrl={BACK_URL}
                currentInput={currentInput}
                currentNumpadInput={currentNumpadInput}>
                <div className='content-divider'>
                    <div className='content-wrapper'>
                        <SettingInputComponent
                            placeholder={t('write_intercom_name')}
                            label={t('intercom_name')}
                            value={name}
                            setValue={setName}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}/>
                        <SettingSelectComponent
                            label={t('camera')}
                            options={cameraOptionList}
                            onChange={setCameraOption}
                            value={cameraOption}/>
                        <SettingInputComponent
                            placeholder={t('ID')}
                            label={t('ID')}
                            value={sipID}
                            setValue={setSipID}
                            setActiveInput={setActiveNumpadInput}
                            onBlur={onBlur}/>
                    </div>
                    <div className='content-wrapper'>
                        <SettingSelectComponent
                            label={t('doors') + (selectedDoorOptionList.length === 0 ? '' : ' (' + selectedDoorOptionList.length.toString() + ')')}
                            options={doorOptionList}
                            isMulti={true}
                            // @ts-ignore
                            onChange={setSelectedDoorOptionList}
                            value={selectedDoorOptionList}/>
                    </div>
                </div>
            </SettingsPageContainer>
            {
                renderKeyboard()
            }
            {
                renderNumpadKeyboard()
            }
        </PasswordContainer>
    );
});
